import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  ArrowLeft,
  Lock,
  ChevronRight,
  Loader2,
  Sparkles,
  GraduationCap,
  Heart,
  Target,
  Beaker,
  Crown,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

const CurriculumPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, fetchUser, languageVersion } = useAuth();
  const [modules, setModules] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedLockedModule, setSelectedLockedModule] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  const effectiveTier = user?.effective_tier || user?.tier || 'free';

  // Refetch curriculum when language changes
  useEffect(() => {
    loadCurriculum();
  }, [languageVersion]);

  const loadCurriculum = async () => {
    try {
      const response = await api.getCurriculum();
      setModules(response.data.modules);
      setRecommended(response.data.recommended || []);
    } catch (error) {
      toast.error(t('curriculum.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLockedModuleClick = (module) => {
    setSelectedLockedModule(module);
    setShowUpgradeModal(true);
  };

  const handleUpgrade = async (tier = 'basic') => {
    setUpgrading(true);
    try {
      const lookupKey = tier === 'pro' ? 'pro_monthly' : 'basic_monthly';
      const response = await api.createCheckout(lookupKey, window.location.origin);
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error(t('curriculum.upgradeFailed'));
        setUpgrading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(t('curriculum.upgradeFailed'));
      setUpgrading(false);
    }
  };

  const getTierBadge = (tier) => {
    if (tier === 'free') return { label: t('tiers.free'), className: 'bg-ocean-blue' };
    if (tier === 'basic') return { label: t('tiers.basic'), className: 'bg-sky-blue' };
    if (tier === 'pro') return { label: t('tiers.pro'), className: 'bg-coral' };
    return { label: tier, className: '' };
  };

  // Helper function to get translated module title
  const getModuleTitle = (module) => {
    // Try the new modules.module_X.title format first
    const newKey = `modules.${module.id}.title`;
    const translated = t(newKey);
    if (translated !== newKey) return translated;
    
    // Fallback to old format
    const oldKey = `curriculum.${module.id}_title`;
    const oldTranslated = t(oldKey);
    return oldTranslated !== oldKey ? oldTranslated : module.title;
  };

  // Helper function to get translated module description
  const getModuleDesc = (module) => {
    // Try the new modules.module_X.description format first
    const newKey = `modules.${module.id}.description`;
    const translated = t(newKey);
    if (translated !== newKey) return translated;
    
    // Fallback to old format
    const oldKey = `curriculum.${module.id}_desc`;
    const oldTranslated = t(oldKey);
    return oldTranslated !== oldKey ? oldTranslated : module.description;
  };

  const getModuleIcon = (moduleId) => {
    if (moduleId.includes('confidence') || moduleId.includes('wins') || moduleId.includes('network')) {
      return Heart;
    }
    if (moduleId.includes('traction') || moduleId.includes('branding')) {
      return Target;
    }
    if (moduleId.includes('lab')) {
      return Beaker;
    }
    return BookOpen;
  };

  const getModuleColor = (moduleId, tier) => {
    if (moduleId === 'module_0') return 'from-ocean-blue to-sky-blue';
    if (moduleId.includes('layer_0')) return 'from-coral to-coral/70';
    if (moduleId.includes('layer_1')) return 'from-mint to-mint/70';
    if (tier === 'pro') return 'from-purple-500 to-purple-400';
    return 'from-ocean-blue to-sky-blue';
  };

  const layers = [
    {
      title: t('curriculum.gettingStarted'),
      description: t('curriculum.gettingStartedDesc'),
      modules: modules.filter(m => m.id === 'module_0')
    },
    {
      title: t('curriculum.coreSkills'),
      description: t('curriculum.coreSkillsDesc'),
      modules: modules.filter(m => ['module_1', 'module_2', 'module_3', 'module_4', 'module_5'].includes(m.id))
    },
    {
      title: t('curriculum.advancedSkills'),
      description: t('curriculum.advancedSkillsDesc'),
      badge: t('tiers.pro'),
      modules: modules.filter(m => {
        const num = parseInt(m.id.replace('module_', ''));
        return num >= 6 && num <= 41;
      })
    },
    {
      title: t('curriculum.technicalVerticals'),
      description: t('curriculum.technicalVerticalsDesc'),
      badge: t('tiers.pro'),
      modules: modules.filter(m => {
        const num = parseInt(m.id.replace('module_', ''));
        return num >= 42 && num <= 64;
      })
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ocean-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-ocean-blue" />
                <span className="font-bold text-foreground">{t('nav.curriculum')}</span>
              </div>
            </div>
            <Badge variant="outline" className={`capitalize ${
              effectiveTier === 'pro' ? 'border-coral text-coral' :
              effectiveTier === 'basic' ? 'border-ocean-blue text-ocean-blue' :
              'border-muted-foreground text-muted-foreground'
            }`}>
              {effectiveTier === 'pro' && <Crown className="w-3 h-3 mr-1" />}
              {t(`tiers.${effectiveTier}`)} {t('settings.tier').toLowerCase()}
            </Badge>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
            {t('curriculum.yourLearningPath')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('curriculum.progressDesc')}
          </p>
        </motion.div>

        {/* Overall Progress Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className="rounded-2xl bg-gradient-to-br from-ocean-blue/5 to-sky-blue/5 border-ocean-blue/20">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-ocean-blue to-sky-blue flex items-center justify-center">
                    <GraduationCap className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{t('curriculum.yourProgress')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('curriculum.lessonsCompleted', { completed: user?.lessons_completed?.length || 0, total: 650 })}
                    </p>
                  </div>
                </div>
                <div className="w-full sm:w-64">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>{t('curriculum.overall')}</span>
                    <span>{Math.round(((user?.lessons_completed?.length || 0) / 650) * 100)}%</span>
                  </div>
                  <Progress 
                    value={((user?.lessons_completed?.length || 0) / 650) * 100} 
                    className="h-3" 
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Recommended For You */}
        {recommended.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-ocean-blue" />
              <h2 className="text-lg font-bold text-foreground">{t('curriculum.recommendedForYou')}</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2">
              {recommended.map((rec) => {
                const module = modules.find(m => m.id === rec.module_id);
                if (!module) return null;
                return (
                  <Link 
                    key={rec.module_id} 
                    to={`/curriculum/${rec.module_id}`}
                    className="flex-shrink-0"
                  >
                    <Card className="w-64 rounded-xl border-2 border-ocean-blue/30 hover:border-ocean-blue transition-colors">
                      <CardContent className="p-4">
                        <Badge className="mb-2 bg-ocean-blue/10 text-ocean-blue border-0">
                          {t('curriculum.recommended')}
                        </Badge>
                        <h3 className="font-semibold text-foreground">{getModuleTitle(module)}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{rec.reason}</p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Curriculum Layers */}
        <div className="space-y-12">
          {layers.map((layer, layerIndex) => (
            layer.modules.length > 0 && (
              <motion.div
                key={layer.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: layerIndex * 0.1 }}
              >
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                      {layer.title}
                      {layer.badge && (
                        <Badge className="bg-coral text-white">
                          <Crown className="w-3 h-3 mr-1" />
                          {layer.badge}
                        </Badge>
                      )}
                    </h2>
                    <p className="text-muted-foreground">{layer.description}</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {layer.modules.map((module, moduleIndex) => {
                    const ModuleIcon = getModuleIcon(module.id);
                    const gradientColor = getModuleColor(module.id, module.tier);
                    const tierBadge = getTierBadge(module.tier);
                    const completedLessons = user?.lessons_completed?.filter(l => l.startsWith(module.id.slice(0, 3)))?.length || 0;
                    const progressPercent = module.lesson_count > 0 ? (completedLessons / module.lesson_count) * 100 : 0;
                    
                    return (
                      <motion.div
                        key={module.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: layerIndex * 0.1 + moduleIndex * 0.05 }}
                      >
                        <Card 
                          className={`h-full rounded-2xl border-0 shadow-lg overflow-hidden ${
                            module.locked ? 'opacity-80' : 'hover:shadow-xl transition-shadow'
                          }`}
                          data-testid={`curriculum-module-${module.id}`}
                        >
                          <div className={`h-2 bg-gradient-to-r ${gradientColor}`} />
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between">
                              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradientColor} flex items-center justify-center`}>
                                {module.locked ? (
                                  <Lock className="w-6 h-6 text-white" />
                                ) : module.completed ? (
                                  <CheckCircle className="w-6 h-6 text-white" />
                                ) : (
                                  <ModuleIcon className="w-6 h-6 text-white" />
                                )}
                              </div>
                              <Badge className={tierBadge.className}>
                                {module.tier === 'pro' && <Crown className="w-3 h-3 mr-1" />}
                                {tierBadge.label}
                              </Badge>
                            </div>
                            <CardTitle className="mt-4">{getModuleTitle(module)}</CardTitle>
                            <CardDescription>{getModuleDesc(module)}</CardDescription>
                            {module.emotional_tone && (
                              <p className="text-xs text-ocean-blue/80 italic mt-2">
                                &ldquo;{module.emotional_tone}&rdquo;
                              </p>
                            )}
                          </CardHeader>
                          <CardContent>
                            {/* Progress Bar */}
                            {!module.locked && progressPercent > 0 && (
                              <div className="mb-3">
                                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                  <span>{t('curriculum.progress')}</span>
                                  <span>{Math.round(progressPercent)}%</span>
                                </div>
                                <Progress value={progressPercent} className="h-2" />
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-muted-foreground">
                                {module.lesson_count} {t('curriculum.lessons').toLowerCase()}
                              </span>
                              {module.locked ? (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleLockedModuleClick(module)}
                                  className="rounded-full"
                                  data-testid={`unlock-${module.id}`}
                                >
                                  <Lock className="w-4 h-4 mr-2" />
                                  {t('labs.unlock')}
                                </Button>
                              ) : (
                                <Link to={`/curriculum/${module.id}`}>
                                  <Button 
                                    size="sm"
                                    className="rounded-full gradient-ocean text-white"
                                    data-testid={`start-${module.id}`}
                                  >
                                    {module.completed ? t('labs.review') : progressPercent > 0 ? t('common.continue') : t('common.start')}
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )
          ))}
        </div>

        {/* Upgrade CTA */}
        {effectiveTier === 'free' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-16"
          >
            <Card className="p-8 rounded-3xl bg-gradient-to-br from-ocean-blue via-sky-blue to-ocean-blue border-0 text-center text-white">
              <Sparkles className="w-12 h-12 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">
                {t('curriculum.readyForJourney')}
              </h2>
              <p className="text-white/80 mb-6 max-w-lg mx-auto">
                {t('curriculum.upgradeToUnlock')}
              </p>
              <Button 
                size="lg"
                onClick={() => setShowUpgradeModal(true)}
                className="rounded-full bg-white text-ocean-blue hover:bg-white/90 px-8"
                data-testid="upgrade-curriculum-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                {t('dashboard.viewPlans')}
              </Button>
            </Card>
          </motion.div>
        )}
      </main>

      {/* Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-ocean-blue" />
              {selectedLockedModule ? `${t('labs.unlock')} ${selectedLockedModule.title}` : t('labs.upgradeYourPlan')}
            </DialogTitle>
            <DialogDescription>
              {selectedLockedModule 
                ? t('labs.labRequires', { tier: selectedLockedModule.tier === 'pro' ? 'Pro' : 'Basic' })
                : t('labs.choosePlan')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Basic Plan */}
            <div className={`p-4 rounded-xl border-2 ${effectiveTier === 'basic' ? 'border-ocean-blue bg-ocean-blue/5' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg">{t('tiers.basic')}</h3>
                <Badge className="bg-ocean-blue">$29/{t('curriculum.month')}</Badge>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                <li>✓ {t('curriculum.allLayer01')}</li>
                <li>✓ {t('curriculum.ideaPitchLab')}</li>
                <li>✓ {t('curriculum.regionalRoomsAccess')}</li>
                <li>✓ {t('curriculum.adaptivePlans')}</li>
              </ul>
              <Button 
                className="w-full gradient-ocean" 
                onClick={() => handleUpgrade('basic')}
                disabled={upgrading || effectiveTier === 'basic' || effectiveTier === 'pro'}
              >
                {upgrading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {effectiveTier === 'basic' || effectiveTier === 'pro' ? t('labs.unlocked') : t('labs.upgradeToBasic')}
              </Button>
            </div>
            
            {/* Pro Plan */}
            <div className={`p-4 rounded-xl border-2 ${effectiveTier === 'pro' ? 'border-coral bg-coral/5' : 'border-border'}`}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Crown className="w-5 h-5 text-coral" />
                  {t('tiers.pro')}
                </h3>
                <Badge className="bg-coral">$99/{t('curriculum.month')}</Badge>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                <li>✓ {t('curriculum.everythingBasic')}</li>
                <li>✓ {t('curriculum.advancedModules')}</li>
                <li>✓ {t('curriculum.allLabsInvestor')}</li>
                <li>✓ {t('curriculum.certificationSystem')}</li>
                <li>✓ {t('curriculum.prioritySupport')}</li>
              </ul>
              <Button 
                className="w-full bg-coral hover:bg-coral/90" 
                onClick={() => handleUpgrade('pro')}
                disabled={upgrading || effectiveTier === 'pro'}
              >
                {upgrading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {effectiveTier === 'pro' ? t('labs.currentPlan') : t('labs.upgradeToPro')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurriculumPage;
